import React from 'react';
import TitleBanner from './TitleBanner';
import TestimonialsSlider from './TestimonialsSlider';
import ApplicationForm from './ApplicationForm';

const Respite = () => {
  const keyFactors1 = [
    {
      number: 1,
      text: "The provision of intensive support with skilled and experienced staff and allied health"
    },
    {
      number: 2,
      text: "Psycho social rehabilitation; supporting individuals to alleviate anxiety and accept injuries or conditions which may hinder recovery or wellbeing."
    },
    {
      number: 3,
      text: "Ongoing assessment, clearly articulating progress and identifying areas for further development."
    },
    {
      number: 4,
      text: "Fostering and strengthening existing relationships with family and friends and natural supports."
    },
    {
      number: 5,
      text: "Support for individuals to engage with the local community, allied health professionals and linking with existing community supports."
    },
    {
      number: 6,
      text: "Regular liaison and communication with key stakeholders regarding service outcomes."
    },
    {
      number: 7,
      text: "Identification of longer term support requirements and recommendations regarding future community based accommodation options."
    }
  ];

  const keyFactors2 = [
    {
      number: 1,
      text: "Personalised support with skilled and experienced staff."
    },
    {
      number: 2,
      text: "Fully furnished, self-contained, accessible homes close to all amenities."
    },
    {
      number: 3,
      text: "Amazing meals provided throughout the day including breakfast, lunch, dinner, morning, and afternoon tea."
    },
    {
      number: 4,
      text: "Personal care and support throughout the day and night."
    },
    {
      number: 5,
      text: "Community inclusion activities."
    }
  ];

  const staffInfo = [
    {
      title: "Have a minimum of Certificate III in Disability.",
      icon: "/LGA-TransitionandRespiteCare-DisabilityCertification-Icon.svg",
      bgColor: "bg-yellow-300",
      tilt: "rotate-3"
    },
    {
      title: "Are MAPA trained (Robust Participants)",
      icon: "/LGA-TransitionandRespiteCare-MPATrained-Icon.svg",
      bgColor: "bg-green-500",
      tilt: "-rotate-3"
    },
    {
      title: "Verified security checks by the Department for Communities and Social Inclusion",
      icon: "/LGA-TransitionandRespiteCare-VerifiedChecks-Icon.svg",
      bgColor: "bg-yellow-300",
      tilt: "rotate-3"
    },
    {
      title: "Undergo annual reviews of their first aid, manual handling, and medication management skills.",
      icon: "/LGA-TransitionandRespiteCare-Reviews-Icon.svg",
      bgColor: "bg-green-500",
      tilt: "-rotate-3"
    },
    {
      title: "Are culturally diverse to make you feel more comfortable.",
      icon: "/LGA-TransitionandRespiteCare-CulturallyDiverse-Icon.svg",
      bgColor: "bg-yellow-300",
      tilt: "rotate-3"
    },
    {
      title: "Love and dedicated to what they do.",
      icon: "/LGA-TransitionandRespiteCare-LoveandDedication-Icon.svg",
      bgColor: "bg-green-500",
      tilt: "-rotate-3"
    }
  ];

  return (
    <div> 
      <TitleBanner image="/Website_Title_Images/Transition_and_Respite.svg" title={"Transition & Respite Care"} subheading={"Find expert care and support at every stage!"} />
      <div className="max-w-6xl mx-auto p-4 sm:p-6 py-8 sm:py-12 bg-white">
        
        <h1 className="text-3xl sm:text-4xl font-bold mb-6 sm:mb-8 font-arial-rounded text-primary">Transitional Accommodation</h1>
        
        <div className="flex flex-col md:flex-row items-center gap-6 sm:gap-8 mb-8 sm:mb-12">
          <div className="relative w-64 h-64 md:w-96 md:h-96">
            {/* Translated border circle */}
            
            {/* Yellow background circle */}
            <div className="absolute inset-0 overflow-hidden">
              <img 
                src="/Framed/Framed Graphic_Transitional Care.svg"
                alt="Transitional accommodation support"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
          
          <div className="flex-1 space-y-3 sm:space-y-4">
            <p className="text-gray-800 text-sm sm:text-lg">
              Livability Care Australia can provide short to medium term transitional accommodation in our purpose-built respite properties. These homes have been extensively modified and refurbished to provide homely and welcoming shared living arrangements for individuals with varying levels of physical and intellectual disabilities and acquired brain injury. Our Transitional Service is available to adults aged 18 - 65 years.
            </p>
            
            <p className="text-gray-800 text-sm sm:text-lg">
              We use a person-centered approach to foster a stable and secure environment and actively support each individual to maximise their independence and plan their future with the support of their family and other stakeholders.
            </p>
          </div>
        </div>

        <h2 className="text-2xl sm:text-3xl font-arial-rounded font-bold text-primary text-center mb-8 sm:mb-12">
          Key factors of our Transitional Accommodation Service Model:
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 sm:gap-x-12 gap-y-6 sm:gap-y-8 max-w-4xl mx-auto">
          {keyFactors1.map((factor) => (
            <div 
              key={factor.number}
              className="flex items-center gap-2 sm:gap-4 relative group transition-transform duration-300 hover:-translate-y-1"
            >
              <div className="absolute -left-2 top-1/2 -translate-y-1/2">
                <div className="text-5xl sm:text-7xl font-bold text-[#d6bbde] opacity-70 group-hover:opacity-100 transition-opacity" 
                     style={{ textShadow: '2px 2px 6px rgba(0,0,0,0.25)' }}>
                  {factor.number}
                </div>
              </div>
              <div className="flex-1 bg-primary text-white p-3 sm:p-4 rounded-lg ml-8 sm:ml-10 shadow-md 
                            group-hover:shadow-lg transition-all duration-300">
                <p className="text-xs sm:text-base leading-relaxed">
                  {factor.text}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="max-w-6xl mx-auto p-4 sm:p-6 py-8 sm:py-12 bg-white">
        <h1 className="text-3xl sm:text-4xl font-bold mb-6 sm:mb-8 font-arial-rounded text-primary">Adult Respite</h1>
        
        <div className="flex flex-col-reverse md:flex-row items-center gap-6 sm:gap-8 mb-8 sm:mb-12">
          <div className="flex-1 space-y-3 sm:space-y-4">
            <p className="text-gray-800 text-sm sm:text-lg">
              Finding the right accommodation setting to meet your needs can be very overwhelming, especially if you or your carer need a break, you are waiting for home modifications or you find yourself suddenly being discharged from hospital and need to find accommodation that will ensure you are being taken care of for a short period of time. That is why we offer a respite program that provides quality, short-term accommodation in a comfortable and homely environment where your loved one will be well looked after. Our welcoming respite, short-stay accommodation centers are a relaxed stay for people with a disability for a weekend or a few weeks.
            </p>
          </div>
          <div className="relative w-64 h-64 md:w-96 md:h-96">
            {/* Translated border circle */}
            <div className="absolute inset-0 overflow-hidden">
              <img 
                src="/Framed/Framed Graphic_Respite.svg"
                alt="Transitional accommodation support"
                className="w-full h-full object-cover"
              />
              </div>
          </div>
        </div>

        <h2 className="text-2xl sm:text-3xl font-arial-rounded font-bold text-primary text-center mb-8 sm:mb-12">
          Key factors of our Adult Respite Service Model:
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 sm:gap-x-12 gap-y-6 sm:gap-y-8 max-w-4xl mx-auto">
          {keyFactors2.map((factor) => (
            <div 
              key={factor.number}
              className="flex items-center gap-2 sm:gap-4 relative group transition-transform duration-300 hover:-translate-y-1"
            >
              <div className="absolute -left-2 top-1/2 -translate-y-1/2">
                <div className="text-5xl sm:text-7xl font-bold text-[#d6bbde] opacity-70 group-hover:opacity-100 transition-opacity" 
                     style={{ textShadow: '2px 2px 6px rgba(0,0,0,0.25)' }}>
                  {factor.number}
                </div>
              </div>
              <div className="flex-1 bg-primary text-white p-3 sm:p-4 rounded-lg ml-8 sm:ml-10 shadow-md 
                            group-hover:shadow-lg transition-all duration-300">
                <p className="text-xs sm:text-base leading-relaxed">
                  {factor.text}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="max-w-6xl mx-auto p-4 sm:p-8 py-12 sm:py-24">
        <h1 className="text-3xl sm:text-5xl font-arial-rounded font-bold text-primary text-center mb-8 sm:mb-12">Our Support Staff</h1>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6 sm:gap-24">
          {staffInfo.map((item, index) => (
            <div 
              key={index} 
              className={`transform ${item.tilt} mb-12 sm:mb-8 transition-transform hover:scale-105`}
            >
              <div className="border-2 border-primary rounded-lg p-4 sm:p-6 bg-white h-full">
                <div className={`w-full h-full sm:w-full sm:h-full mx-auto mb-3 sm:mb-4 rounded-full flex items-center justify-center`}>
                  <img src={"/Icons"+item.icon} alt="" className="w-32 h-auto sm:w-48 sm:h-auto" />
                </div>
              </div>
              <p className="text-center mt-3 sm:mt-4 text-primary font-semibold text-sm sm:text-base">
                {item.title}
              </p>
            </div>
          ))}
        </div>
      </div>
      <ApplicationForm/>
      <TestimonialsSlider />
    </div>
  );
};

export default Respite;